import { useEffect, useRef } from "react";

// Soft glowing cursor trail rendered on a canvas inside the hero.
// Desktop / fine-pointer only; skipped on touch devices.
export default function HeroCursorTrail() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const fine =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!fine) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const parent = canvas.parentElement;
    let raf = 0;
    const points = [];
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const r = parent.getBoundingClientRect();
      canvas.width = r.width * DPR;
      canvas.height = r.height * DPR;
      canvas.style.width = `${r.width}px`;
      canvas.style.height = `${r.height}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();

    const onMove = (e) => {
      const r = parent.getBoundingClientRect();
      points.push({ x: e.clientX - r.left, y: e.clientY - r.top, life: 1 });
      if (points.length > 26) points.shift();
    };

    const draw = () => {
      const r = parent.getBoundingClientRect();
      ctx.clearRect(0, 0, r.width, r.height);
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        p.life *= 0.92;
        const radius = 22 * p.life + 3;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
        g.addColorStop(0, `rgba(91,128,255,${0.28 * p.life})`);
        g.addColorStop(1, "rgba(91,128,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      for (let i = points.length - 1; i >= 0; i--) {
        if (points[i].life < 0.04) points.splice(i, 1);
      }
      raf = requestAnimationFrame(draw);
    };

    parent.addEventListener("mousemove", onMove);
    window.addEventListener("resize", resize);
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-[15] pointer-events-none"
      aria-hidden="true"
      data-testid="hero-cursor-trail"
    />
  );
}
